import React, { useState, useContext, useEffect, useRef } from 'react';
import { notification } from 'antd';
import { SearchOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { UserContext } from '../../App';
import Services from '../../network/Services';
import commonLib from '../../control/commonLib';
import DocumentEditor from './DocumentEditor';
import './DocumentManagement.css';
import ReactDOM from 'react-dom';
import { getClauseQuestions } from './clauseQuestions';

function ConditionSelect({ options, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        !e.target.closest('.cs-panel')
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const goUp = spaceBelow < 300;
      const panelWidth = Math.max(rect.width, 180);
      let leftPos = rect.left;
      if (leftPos + panelWidth > window.innerWidth - 8) {
        leftPos = window.innerWidth - panelWidth - 8;
      }

      setDropdownStyle({
        top: goUp ? undefined : rect.bottom + 6,
        bottom: goUp ? window.innerHeight - rect.top + 6 : undefined,
        left: Math.max(8, leftPos),
        width: panelWidth,
      });
    }
    setIsOpen(prev => !prev);
  };

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
  };

  const selectedOption = options?.find(opt => opt.value === value);
  const btnLabel = selectedOption
    ? (selectedOption.label?.length > 48
      ? selectedOption.label.slice(0, 46) + '…'
      : selectedOption.label)
    : 'Filter paragraphs by condition';

  return (
    <div className="cs-wrapper" ref={wrapperRef}>
      <button
        className={`cs-btn ${value ? 'cs-btn--active' : ''} ${isOpen ? 'cs-btn--open' : ''}`}
        onClick={handleToggle}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="cs-btn__text">{btnLabel}</span>
        <span className="cs-btn__chevron">
          {isOpen
            ? <UpOutlined style={{ fontSize: 10 }} />
            : <DownOutlined style={{ fontSize: 10 }} />
          }
        </span>
      </button>

      {value && (
        <button
          className="cs-clear"
          onClick={(e) => { e.stopPropagation(); handleSelect(null); }}
          type="button"
          title="Clear filter"
        >×</button>
      )}

      {isOpen && ReactDOM.createPortal(
        <div className="cs-panel" style={dropdownStyle} role="listbox">

          <div className="cs-panel__options">
            <div
              className={`cs-opt cs-opt--all ${!value ? 'cs-opt--selected' : ''}`}
              onClick={() => handleSelect(null)}
              role="option"
              aria-selected={!value}
            >
              <span className="cs-opt__label cs-opt__label--all">Show complete document</span>
            </div>

            {options?.map(opt => (
              <div
                key={opt.value}
                className={`cs-opt ${opt.value === value ? 'cs-opt--selected' : ''}`}
                onClick={() => handleSelect(opt.value)}
                role="option"
                aria-selected={opt.value === value}
              >
                <div className="cs-opt__content">
                  <span className="cs-opt__label">{opt.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function VersionSelect({ options, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        !e.target.closest('.cs-panel')
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const goUp = spaceBelow < 300;
      const panelWidth = Math.max(rect.width, 180);
      let leftPos = rect.left;
      if (leftPos + panelWidth > window.innerWidth - 8) {
        leftPos = window.innerWidth - panelWidth - 8;
      }

      setDropdownStyle({
        top: goUp ? undefined : rect.bottom + 6,
        bottom: goUp ? window.innerHeight - rect.top + 6 : undefined,
        left: Math.max(8, leftPos),
        width: panelWidth,
      });
    }
    setIsOpen(prev => !prev);
  };

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
  };

  const selectedOption = options?.find(opt => opt.value === value);
  const btnLabel = selectedOption ? selectedOption.label : 'Latest';

  return (
    <div className="cs-wrapper" ref={wrapperRef}>
      <button
        className={`cs-btn ${value ? 'cs-btn--active' : ''} ${isOpen ? 'cs-btn--open' : ''}`}
        onClick={handleToggle}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="cs-btn__text">{btnLabel}</span>
        <span className="cs-btn__chevron">
          {isOpen
            ? <UpOutlined style={{ fontSize: 10 }} />
            : <DownOutlined style={{ fontSize: 10 }} />
          }
        </span>
      </button>

      {isOpen && ReactDOM.createPortal(
        <div className="cs-panel" style={dropdownStyle} role="listbox">
          <div className="cs-panel__options">
            <div
              className={`cs-opt cs-opt--all ${!value ? 'cs-opt--selected' : ''}`}
              onClick={() => handleSelect(null)}
              role="option"
              aria-selected={!value}
            >
              <span className="cs-opt__label cs-opt__label--all">Latest</span>
            </div>

            {options?.map(opt => (
              <div
                key={opt.value}
                className={`cs-opt ${opt.value === value ? 'cs-opt--selected' : ''}`}
                onClick={() => handleSelect(opt.value)}
                role="option"
                aria-selected={opt.value === value}
              >
                <div className="cs-opt__content">
                  <span className="cs-opt__label">{opt.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function ClauseQuestionSelect({ options, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target) && !e.target.closest('.cs-panel')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    console.log('DEBUG: handleToggle fired', { isOpen, optionsLength: options?.length, wrapperRef: !!wrapperRef.current });
    if (!isOpen && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      console.log('DEBUG: button rect', rect);
      const spaceBelow = window.innerHeight - rect.bottom;
      const goUp = spaceBelow < 300;
      const panelWidth = Math.max(rect.width, 260);
      let leftPos = rect.left;
      if (leftPos + panelWidth > window.innerWidth - 8) leftPos = window.innerWidth - panelWidth - 8;
      const newStyle = {
        top: goUp ? undefined : rect.bottom + 6,
        bottom: goUp ? window.innerHeight - rect.top + 6 : undefined,
        left: Math.max(8, leftPos),
        width: panelWidth,
      };
      console.log('DEBUG: computed dropdownStyle', newStyle);
      setDropdownStyle(newStyle);
    }
    setIsOpen(prev => {
      console.log('DEBUG: isOpen changing from', prev, 'to', !prev);
      return !prev;
    });
  };

  const handleSelect = (val) => { onChange(val); setIsOpen(false); };

  const selectedOption = options?.find(opt => opt.id === value);
  const btnLabel = selectedOption
    ? (selectedOption.question?.length > 48 ? selectedOption.question.slice(0, 46) + '…' : selectedOption.question)
    : 'View non-editable clauses';

  console.log('DEBUG: ClauseQuestionSelect render', { isOpen, optionsCount: options?.length });

  return (
    <div className="cs-wrapper" ref={wrapperRef}>
      <button
        className={`cs-btn ${value ? 'cs-btn--active' : ''} ${isOpen ? 'cs-btn--open' : ''}`}
        onClick={handleToggle}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="cs-btn__text">{btnLabel}</span>
        <span className="cs-btn__chevron">
          {isOpen ? <UpOutlined style={{ fontSize: 10 }} /> : <DownOutlined style={{ fontSize: 10 }} />}
        </span>
      </button>

      {value && (
        <button className="cs-clear" onClick={(e) => { e.stopPropagation(); handleSelect(null); }} type="button" title="Clear">×</button>
      )}

      {isOpen && ReactDOM.createPortal(
        <div className="cs-panel" style={{ ...dropdownStyle, background: 'yellow' }} role="listbox">
          <div className="cs-panel__options">
            {options?.length === 0 && <div style={{ padding: 10 }}>NO OPTIONS PASSED IN</div>}
            {options?.map(opt => (
              <div
                key={opt.id}
                className={`cs-opt ${opt.id === value ? 'cs-opt--selected' : ''}`}
                onClick={() => handleSelect(opt.id)}
                role="option"
                aria-selected={opt.id === value}
              >
                <div className="cs-opt__content">
                  <span className="cs-opt__label">{opt.question}</span>
                </div>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function DocumentManagement() {
  const { setLoader } = useContext(UserContext);
  const [api, contextHolder] = notification.useNotification();

  const [treeData, setTreeData] = useState([]);
  const [expandedDocTypes, setExpandedDocTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [stateSearchTerms, setStateSearchTerms] = useState({});
  const [isIntake, setIsIntake] = useState(0);

  const [selectedRadio, setSelectedRadio] = useState({});
  const [activeDocTypeId, setActiveDocTypeId] = useState(null);
  const [activeStateId, setActiveStateId] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [conditionOptions, setConditionOptions] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [selectedVersionXml, setSelectedVersionXml] = useState(null);
    const [selectedClauseId, setSelectedClauseId] = useState(null);

  useEffect(() => {
    fetchTemplateTree();
  }, [isIntake]);

  useEffect(() => {
    if (expandedDocTypes?.length === 1 && !activeDocTypeId) {
      const docTypeId = expandedDocTypes[0];
      const stateId = selectedRadio[docTypeId];
      if (stateId) handleRadioSelect(docTypeId, stateId);
    }
  }, [expandedDocTypes]);

  console.log(expandedDocTypes, 'sdsksdskdsldsd', activeDocTypeId)

  const fetchTemplateTree = () => {
    setLoader(true);
    Services.getDocumentTemplateTree(isIntake)
      .then(res => {
        const data = res?.data?.data || res?.data || [];
        setTreeData(data);
        setExpandedDocTypes([]);
        setStateSearchTerms({});
        setSelectedRadio({});
        setActiveDocTypeId(null);
        setActiveStateId(null);
        setPreviewData(null);
        setConditionOptions([]);
       setSelectedCondition(null);
setSelectedClauseId(null);
        setVersions([]);
        setSelectedVersionId(null);
        setSelectedVersionXml(null);
      })
      .catch(() => {
        api.error({ message: 'Failed to load document templates', placement: 'topRight' });
      })
      .finally(() => setLoader(false));
  };

  const toggleExpand = (docTypeId) => {
    setExpandedDocTypes(prev => {
      const isExpanded = prev.includes(docTypeId);
      if (isExpanded) {
        if (activeDocTypeId === docTypeId) {
          setActiveDocTypeId(null);
          setActiveStateId(null);
          setPreviewData(null);
          setConditionOptions([]);
         setSelectedCondition(null);
setSelectedClauseId(null);
        }
        return prev?.filter(id => id !== docTypeId);
      }
      setActiveDocTypeId(null);
      setActiveStateId(null);
      setPreviewData(null);
      setConditionOptions([]);
     setSelectedCondition(null);
setSelectedClauseId(null);
      return [docTypeId];
    });
  };

  const extractConditions = (data) => {
    const conditionsMap = new Map();
    (data?.sections || []).forEach(section => {
      (section?.paragraphs || []).forEach(para => {
        const cond = para?.condition;
        if (cond && cond.conditionType && cond.label) {
          if (!conditionsMap.has(cond.label)) {
            conditionsMap.set(cond.label, {
              value: cond.label,
              label: cond.label,
              conditionType: cond.conditionType,
              conditionValue: cond.conditionValue,
            });
          }
        }
      });
    });
    return Array.from(conditionsMap.values());
  };

  const fetchVersions = (docTypeId, subtenantId) => {
    Services.getDocumentVersions(docTypeId, subtenantId ?? null)
      .then(res => {
        const raw = res?.data?.versions || res?.data?.data?.versions || [];
        const flat = [];
        (Array.isArray(raw) ? raw : [])?.forEach(v => {
          (v?.subVersions || [])?.forEach(sv => {
            flat.push({
              versioningId: v.versioningId,
              versionSubNumber: sv.versionSubNumber,
              updatedOn: sv.updatedOn,
              updatedXmlContent: sv.updatedXmlContent || null,
              label: `v${v.versioningId}.${sv.versionSubNumber}`,
            });
          });
        });
        setVersions(flat);
      })
      .catch(() => setVersions([]));
  };

  const handleRadioSelect = (docTypeId, stateId) => {
    if (activeDocTypeId === docTypeId && activeStateId === stateId) {
      setActiveDocTypeId(null);
      setActiveStateId(null);
      setPreviewData(null);
      setConditionOptions([]);
     setSelectedCondition(null);
setSelectedClauseId(null);
      setSelectedRadio(prev => {
        const updated = { ...prev };
        delete updated[docTypeId];
        return updated;
      });
      return;
    }

    setSelectedRadio({ [docTypeId]: stateId });
    setSelectedVersionId(null);
    setLoader(true);
    Services.previewEditDocTool([stateId], [docTypeId], 1, isIntake)
      .then(res => {
        const data = res?.data;
        setPreviewData(data);
        setActiveDocTypeId(docTypeId);
        setActiveStateId(stateId);
       setSelectedCondition(null);
setSelectedClauseId(null);
        setConditionOptions(extractConditions(data));
        fetchVersions(docTypeId, data?.subtenantId);
      })
      .catch(() => {
        api.error({ message: 'Failed to load document', placement: 'topRight' });
      })
      .finally(() => setLoader(false));
  };

  const handleVersionChange = (compositeKey) => {
    console.log('handleVersionChange called', { compositeKey, versionsCount: versions?.length });
    setSelectedVersionId(compositeKey);
    if (!compositeKey) {
      console.log('Clearing version XML (Latest selected)');
      setSelectedVersionXml(null);
      return;
    }
    const found = versions?.find(v => `${v.versioningId}_${v.versionSubNumber}` === compositeKey);
    console.log('Found version:', {
      found: !!found,
      versioningId: found?.versioningId,
      versionSubNumber: found?.versionSubNumber,
      xmlLength: found?.updatedXmlContent?.length
    });
    setSelectedVersionXml(found?.updatedXmlContent || null);
  };

  const handleReset = () => {
    const upsertedBy = commonLib.getSessionStoarge('loggedUserId') || '';
    const versioningId = previewData?.versioningId ?? null;
    const subtenantId = previewData?.subtenantId ?? null;
    setLoader(true);
    Services.resetDocumentTemplate(versioningId, upsertedBy, subtenantId)
      .then(() => {
        api.success({ message: 'Document reset successfully.', placement: 'topRight' });
        if (activeDocTypeId && activeStateId) {
          Services.previewEditDocTool([activeStateId], [activeDocTypeId], 1, isIntake)
            .then(res => {
              const data = res?.data;
              setPreviewData(data);
             setSelectedCondition(null);
setSelectedClauseId(null);
              setConditionOptions(extractConditions(data));
            })
            .catch(() => { })
            .finally(() => setLoader(false));
        } else {
          setLoader(false);
        }
      })
      .catch(() => {
        api.error({ message: 'Failed to reset document.', placement: 'topRight' });
        setLoader(false);
      });
  };

  const getTextWithBreaks = (el) => {
    if (!el) return '';
    const clone = el.cloneNode(true);
    clone.querySelectorAll('br').forEach(br => {
      br.replaceWith(' ');
    });
    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
  };

  const handleSave = (payload) => {
    setLoader(true);
    Services.saveDocTool(payload)
      .then(() => {
        api.success({ message: 'Document saved successfully', placement: 'topRight' });
        if (activeDocTypeId && activeStateId) {
          Services.previewEditDocTool([activeStateId], [activeDocTypeId], 1, isIntake)
            .then(res => {
              const data = res?.data;
              setPreviewData(data);
             setSelectedCondition(null);
setSelectedClauseId(null);
              setConditionOptions(extractConditions(data));
            })
            .catch(() => { })
            .finally(() => setLoader(false));
        } else {
          setLoader(false);
        }
      })
      .catch(() => {
        api.error({ message: 'Failed to save document', placement: 'topRight' });
        setLoader(false);
      });
  };

  const filteredTree = treeData?.filter(item =>
    item.documentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.states?.some(s => s.stateName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const activeDocName = treeData?.find(d => d.docTypeId === activeDocTypeId)?.documentName;
  const activeStateName = treeData
    .find(d => d.docTypeId === activeDocTypeId)
    ?.states?.find(s => s.stateId === activeStateId)?.stateName;
  const activeStateAbbrev = treeData
    .find(d => d.docTypeId === activeDocTypeId)
    ?.states?.find(s => s.stateId === activeStateId)?.stateAbbrev?.trim();

    const clauseQuestionOptions = getClauseQuestions(activeDocTypeId, isIntake);
  const highlightClauseText = clauseQuestionOptions?.find(o => o.id === selectedClauseId)?.startText || null;

  return (
    <div className="dm-root">
      {contextHolder}

      <div className="dm-header">
        <div className="dm-header-left">
          <div>
            <p className="dm-header-title">Document Management</p>
          </div>
        </div>

        <div
          className={`dm-toggle-pill ${isIntake === 1 ? 'active' : ''}`}
          onClick={() => setIsIntake(prev => prev === 1 ? 0 : 1)}
        >
          <div className="dm-toggle-switch">
            <div className="dm-toggle-thumb" />
          </div>
          <span className="dm-toggle-label">AO Internal</span>
        </div>
      </div>

      <div className="dm-body">
        <div className="dm-library">
          <div className="dm-library-search">
            <div className="dm-search-box">
              <SearchOutlined className="dm-search-icon" />
              <input
                className="dm-search-input"
                type="text"
                placeholder="Search documents or states…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <span className="dm-search-clear" onClick={() => setSearchTerm('')}>×</span>
              )}
            </div>
          </div>

          <div className="dm-library-list">
            {filteredTree?.length > 0 ? filteredTree?.map(item => {
              const isExpanded = expandedDocTypes.includes(item.docTypeId);
              const selectedStateForDoc = selectedRadio[item.docTypeId];
              const stateSearch = (stateSearchTerms[item.docTypeId] || '').toLowerCase();

              const filteredStates = item.states?.filter(state =>
                !stateSearch ||
                state.stateName.toLowerCase().includes(stateSearch) ||
                (state.stateAbbrev || '').trim().toLowerCase().includes(stateSearch)
              );

              // const isLockedForAOInternal = isIntake === 1 && item.documentName === 'Last Will and Testament';
              const isLockedForAOInternal = false;

              return (
                <div key={item.docTypeId} className={`dm-doc-card ${isExpanded ? 'expanded' : ''}`}>
                  <div
                    className="dm-doc-card-header"
                    onClick={() => !isLockedForAOInternal && toggleExpand(item.docTypeId)}
                    style={isLockedForAOInternal ? { cursor: 'not-allowed', opacity: 0.55 } : undefined}
                    title={isLockedForAOInternal ? 'Not available in AO Internal mode' : undefined}
                  >
                    <div className="dm-doc-card-dot" />
                    <span className="dm-doc-card-name">{item.documentName}</span>
                    <span className="dm-doc-card-badge">
                      {item.states?.length} {item.states?.length === 1 ? 'state' : 'states'}
                    </span>
                    <span className="dm-doc-card-chevron">▼</span>
                  </div>

                  {isExpanded && (
                    <div className="dm-states-panel">
                      <div className="dm-state-search">
                        <div className="dm-search-box">
                          <SearchOutlined className="dm-search-icon" style={{ fontSize: 12 }} />
                          <input
                            className="dm-search-input"
                            type="text"
                            placeholder="Filter states…"
                            value={stateSearchTerms[item.docTypeId] || ''}
                            onChange={e => setStateSearchTerms(prev => ({
                              ...prev,
                              [item.docTypeId]: e.target.value
                            }))}
                            onClick={e => e.stopPropagation()}
                          />
                          {stateSearchTerms[item.docTypeId] && (
                            <span
                              className="dm-search-clear"
                              onClick={() => setStateSearchTerms(prev => ({
                                ...prev,
                                [item.docTypeId]: ''
                              }))}
                            >×</span>
                          )}
                        </div>
                      </div>

                      <div className="dm-states-list">
                        {filteredStates?.length > 0 ? filteredStates?.map(state => (
                          <label
                            key={state.stateId}
                            className={`dm-state-item ${selectedStateForDoc === state.stateId ? 'selected' : ''}`}
                          >
                            <input
                              type="radio"
                              className="dm-state-radio"
                              name={`state-radio-${item.docTypeId}`}
                              checked={selectedStateForDoc === state.stateId}
                              onChange={() => handleRadioSelect(item.docTypeId, state.stateId)}
                            />
                            <span className="dm-state-name">
                              {state.stateName}
                              {state.stateAbbrev ? ` (${state.stateAbbrev.trim()})` : ''}
                            </span>
                          </label>
                        )) : (
                          <div className="dm-states-no-result">No states match</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            }) : (
              <div className="dm-library-empty">No document templates found</div>
            )}
          </div>
        </div>

        <div className="dm-main">
          {previewData && activeDocTypeId ? (
            <div className="dm-editor-section">
              <div className="dm-editor-topbar">
                <div className="dm-editor-location">
                  <span className="dm-editor-location-label">Editing</span>
                  <div className="dm-editor-breadcrumb">
                    <span className="dm-editor-breadcrumb-doc">{activeDocName}</span>
                    <span className="dm-editor-breadcrumb-sep">›</span>
                    <span className="dm-editor-breadcrumb-state">
                      {activeStateName}{activeStateAbbrev ? ` (${activeStateAbbrev})` : ''}
                    </span>
                  </div>
                </div>

                {conditionOptions?.length > 0 && (
                  <div className="dm-para-select-wrapper">
                    <ConditionSelect
                      options={conditionOptions}
                      value={selectedCondition}
                      onChange={setSelectedCondition}
                    />
                  </div>
                )}

                {clauseQuestionOptions?.length > 0 && (
                  <div className="dm-para-select-wrapper">
                    <ClauseQuestionSelect
                      options={clauseQuestionOptions}
                      value={selectedClauseId}
                      onChange={setSelectedClauseId}
                    />
                  </div>
                )}

                {selectedVersionXml && (
                  <span className="dm-version-readonly-badge">
                    Version Preview — Read Only
                  </span>
                )}

                {versions?.length > 0 && (
                  <div className="dm-version-wrapper">
                    <span className="dm-version-label">Version</span>
                    <VersionSelect
                      options={versions.map(v => ({
                        value: `${v.versioningId}_${v.versionSubNumber}`,
                        label: `${v.label}${v.updatedOn ? ` — ${new Date(v.updatedOn).toLocaleDateString()}` : ''}`,
                      }))}
                      value={selectedVersionId}
                      onChange={handleVersionChange}
                    />
                  </div>
                )}
              </div>

              <div className="dm-editor-body">
                <DocumentEditor
                  previewData={previewData}
                  stateIds={[activeStateId]}
                  docTypeIds={[activeDocTypeId]}
                  onSave={handleSave}
                  onReset={handleReset}
                  selectedCondition={selectedCondition}
                  isIntake={isIntake}
                  versionXml={selectedVersionXml}
                  highlightClauseText={highlightClauseText}
                />
              </div>
            </div>
          ) : (
            <div className="dm-placeholder">
              <div className="dm-placeholder-icon">📋</div>
              <div className="dm-placeholder-title">No document open</div>
              <p className="dm-placeholder-text">
                Select a document type from the library on the left, then choose a state to start editing.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentManagement;